import { useCallback, useEffect, useState } from "react";
import {
  AppBar,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { ListLayout, TableCard } from "ui-components/Layout";
import { EditButton, CancelButton } from "ui-components/IconButton";
import { Success, Error, MessageDialog } from "ui-components/Alert";
import BasicTabs from "ui-components/BasicTabs";

import { useCustomerValue } from "providers/CustomerContext";

import { statusObj } from "layouts/views/common/enum";
import { getESOList } from "services/data.outbound.service";
import { getESOStatusCount } from "services/data.outbound.service";
import CheckboxTable2 from "ui-components/tables/CheckboxTable2";
import { convertSO } from "services/data.outbound.service";
import { LoadingDialog } from "ui-components/Alert";
import { SelectFields } from "ui-components/Form";
import { Texts } from "ui-components/Form";
import { countryOptions } from "layouts/views/common/options";
import { editESO } from "services/data.outbound.service";
import { Error_ } from "ui-components/Alert";
import { useTeamValue } from "providers/TeamContext";
import ESOLog from "./components/ESOLog";

function ESOList() {
  const {permissions, allowAll} = useTeamValue();
  const { customerCode } = useCustomerValue();
  const enabled = Boolean(customerCode);
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState("Open");
  const [tabs, setTabs] = useState([
    { label: "Open", count: 0 },
    { label: "Hold", count: 0 },
    { label: "Cancelled", count: 0 },
  ]);
  const [updateESO, setUpdateESO] = useState({ esoId: 0, custOrdNbr: "" });
  const queryClient = useQueryClient();
  const onError = (data) => setAlert(<Error data={data} onChange={setAlert} />);
  const [selectedIds, setSelectedIds] = useState([]);
  const editPermission = allowAll || (Boolean(permissions["ESO"]) && permissions["ESO"].findIndex(b => b==="edit") !== -1);

  useQuery({
    queryKey: ["eso-list", { customerCode, status }],
    queryFn: () => getESOList({ customerCode, status: statusObj[status] }),
    enabled,
    onSuccess: (data) => setData(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["eso-list", "status", { customerCode,status }],
    queryFn: () => getESOStatusCount({ customerCode }),
    enabled,
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const convertMutation = useMutation(convertSO, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["eso-list"]);
      queryClient.invalidateQueries(["snt-status"])
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });

  const onEdit = (req) => {
    setOpen(true);
    setUpdateESO({ esoId: req.esoId, custOrdNbr: req.custOrdNbr });
  };

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Delete SO Number ${req.orderNumber}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          //delMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const [logRequest, setLogRequest] = useState(null);
  const [open1, setOpen1] = useState(false);
  const logProp = { open1, setOpen1, logRequest, setLogRequest };

  const columns = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          sx={{
            padding: 0,
            "& .MuiSvgIcon-root": {
              borderColor: "#97b8d8",
            },
          }}
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          sx={{
            padding: 0,
            "& .MuiSvgIcon-root": {
              borderColor: "#97b8d8",
            },
          }}
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          indeterminate={row.getIsSomeSelected()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
    },
    { header: "Cust Order Num", accessorKey: "custOrdNbr",
      cell: ({ row }) => {
        const { custOrdNbr, customerCode } = row.original;
        return (
          <span
            style={{ color: "#1A73E8", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => {
              setOpen1(true);
              setLogRequest({ custOrdNbr, customerCode, status: statusObj[status] });
            }}
          >
            {custOrdNbr}
          </span>
        );
      },
    },
    { header: "Order Date", accessorKey: "orderDate" },
    { header: "Warehouse Code", accessorKey: "warehouseCode" },
    { header: "Customer Code", accessorKey: "customerCode" },
    { header: "Error Message", accessorKey: "errorMessage" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { esoId, custOrdNbr } = row.original;
        return status === "Open" ? (
          <>
            <EditButton title="Edit ESO" disabled={!editPermission} onClick={() => onEdit({ esoId, custOrdNbr })} />
            {/* <CancelButton title="Cancel SO" onClick={() => onDelete({ orderNumber })} /> */}
          </>
        ) : (
          <></>
        );
      },
    },
  ];
  const handleChange = useCallback((data) => {
    setSelectedIds(data);
  }, []);
  const onConvert = () => {
    convertMutation.mutate({ esoIds: selectedIds.map((x) => x.esoId) });
  };
  const convertStatus = convertMutation.status === "loading";
  return (
    <ListLayout>
      <ESOLog {...logProp} />
      {convertStatus && <LoadingDialog />}
      {dialog}
      <UpdateModal open={open} setOpen={setOpen} updateESO={updateESO} />
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" margin="10px">
          <MDButton
            variant="gradient"
            color="info"
            onClick={onConvert}
            disabled={selectedIds.length === 0 || convertStatus || !editPermission}
          >
            convert so
          </MDButton>
        </MDBox>
        <CheckboxTable2 columns={columns} data={data} onSelectedRowsChange={handleChange} />
      </TableCard>
    </ListLayout>
  );
}

export default ESOList;

const UpdateModal = ({ open, setOpen, updateESO }) => {
  const [country, setCountry] = useState(null);
  const [postcode, setPostcode] = useState(null);
  const [alert, setAlert] = useState(null);
  useEffect(() => {
    setCountry(null);
    setPostcode(null);
    setAlert(null);
  }, [open]);
  const updateMutation = useMutation(editESO, {});
  const updateStatus = updateMutation.status === "loading";
  const queryClient = useQueryClient();
  const onSubmit = () => {
    updateMutation.mutate(
      { esoId: updateESO.esoId, country, postcode },
      {
        onSuccess: () => {
          setOpen(false);
          queryClient.invalidateQueries(["eso-list"]);
        },
        onError: (data) => setAlert(<Error_ data={data} onChange={setAlert} />),
      }
    );
  };

  return (
    <Dialog sx={{ padding: 5 }} open={open}>
      <DialogTitle>Update ESO ({updateESO.custOrdNbr})</DialogTitle>
      {updateStatus && <LoadingDialog />}
      {alert}
      <Grid width="500px" padding={2} container spacing={3}>
        <Grid item xs={6}>
          <SelectFields
            field={{
              name: "country",
              label: "Country",
              value: country,
              errors: {},
              required: true,
            }}
            options={["Malaysia", "Singapore", "Brunei", "UK"]}
            onChange={(e, v) => setCountry(v)}
          />
        </Grid>
        <Grid item xs={6}>
          <Texts
            field={{ name: "postcode", label: "Postcode", value: postcode, maxLength: country === "Singapore" ? 6 : 5 }}
            onChange={({ target }) => setPostcode(target.value)}
          />
        </Grid>
      </Grid>
      <DialogActions style={{ display: "flex", justifyContent: "center" }}>
        <MDButton variant="gradient" color="info" onClick={onSubmit} disabled={country === null || updateStatus}>
          Submit
        </MDButton>
        <MDButton
          variant="outlined"
          color="info"
          onClick={() => {
            setOpen(false);
          }}
        >
          Close
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};
